import {useCallback, useState} from 'react';

import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';

import {attachModal} from '~/shared/components/ReduxModal';
import {getLocalizationService} from '~/shared/services/localisationService';
import actions, {setCurrentModal} from '~/shared/store/actions';
import {media} from '~/shared/theme/media';
import ClickOutsideDiv from '~/shared/components/ClickOutsideDiv';
import {body16Bold, headingXS, subheadingSBold} from '~/shared/theme/typography';
import PieButton from '~/shared/theme/pie/PieButton';
import {selectUserAddresses, selectUserData} from '~/shared/store/storeModules/user/userSelectors';
import {formatAddress} from '~/shared/services/addressHelper';
import RadioButton from '~/shared/components/RadioButton';
import {reIssueMoneycard, replaceMoneycard} from '~/shared/services/bffService/api';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import PieCloseModalButton from '~/shared/components/Modals/styled/PieCloseModalButton';
import {MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import {trackEvent} from '~/shared/services/analytics';
import warning from '~/shared/assets/warning.svg';

import HouseIcon from './assets/houseIcon.svg?react';
import OfficeIcon from './assets/officeIcon.svg?react';
import tenbisFlyCard from './assets/tenbisFlyCard.svg';
import warningImage from './assets/warrningImage.png';

const Root = styled(ClickOutsideDiv)`
  display: flex;
  flex-direction: column;

  &&& {
    width: 100vw;

    /* Use dynamic viewport height so mobile browser chrome (e.g. Chrome's address bar)
       shrinks the modal to the visible area instead of clipping the centered header off-screen. */
    height: 100dvh;
    border-radius: 0;
    padding-top: 0;
    padding-bottom: 8px;
  }

  &:first-child {
    background-color: ${({theme}) => theme.colors.surface};
  }

  ${media.minLargeMobile`
    &&& {
      padding-bottom: 24px;
      width: 500px;
      height: inherit;
    }
    justify-content: space-between;
    border-radius: 2px;
    box-shadow: ${({theme}) => theme.shadows.shadow1};
  `}
`;

const Header = styled.div`
  height: 64px;
  width: 100%;
  padding: 16px 0;
  background-color: ${({theme}) => theme.colors.surface};
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  line-height: 16px;

  svg {
    ${({theme}) => `
      fill: ${theme.colors.text2};
    `}
  }
`;

const Title = styled.h3`
  ${subheadingSBold}
  color: ${({theme}) => theme.colors.contentDefault};
  text-align: center;
  width: 100%;
  ${media.minLargeMobile`
    ${headingXS}  
  `}
`;

const ListWrapper = styled.div`
  flex: 1;
  padding: 32px 16px 0;
  overflow-x: hidden;
  ${media.minLargeMobile`
    padding: 32px 32px 0;
    overflow-y: auto;
    max-height: 60vh;
  `}
`;

const List = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  flex-direction: column;
  display: flex;
  gap: 24px;
`;

const AddressItem = styled.li.attrs({
  role: 'button',
})`
  width: 100%;
  cursor: pointer;
  margin: 0;
  padding: 0;
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 20px;
  flex: 1;

  &:last-child {
    border-bottom: 0;
    padding: 0 0 8px 0;
  }
`;

const IconAndAddress = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 8px;
  ${flipOnLTR`
    padding-left: 16px;
  `}
`;

const SubTitle = styled.h3`
  ${body16Bold}
  margin-bottom: 24px;
`;

const StyledPieButton = styled(PieButton).attrs({
  variant: 'primaryXL',
})`
  margin: 32px 16px 0;
  ${media.minLargeMobile`
    margin: 32px 32px 0;
  `}
`;

const AddressText = styled.div`
  width: 100%;
`;

const CloseButton = styled(PieCloseModalButton)`
  top: 16px;
`;

type ReIssueOrReplaceMoneycardModalProps = {
  args: {
    moneycardId: MoneyCard['id'];
    isReIssue: boolean;
    onSuccess?: (() => void) | (() => Promise<void>);
  };
};

// if isReIssue is false it means replace.
const ReIssueOrReplaceMoneycardModal = ({args: {moneycardId, isReIssue, onSuccess}}: ReIssueOrReplaceMoneycardModalProps) => {
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {t} = getLocalizationService();
  const dispatch = useDispatch();
  const addresses = useSelector(selectUserAddresses);
  const userData = useSelector(selectUserData);

  const handleCloseModal = useCallback(() => {
    dispatch(actions.setCurrentModal(null));
  }, [dispatch]);

  const handleSubmitError = async (error: unknown, submitCallback: () => Promise<unknown>) => {
    if (is401Error(error)) {
      await handleRefreshToken(error, submitCallback);
      return;
    }
    dispatch(actions.setCurrentModal('reIssueOrReplaceMoneycardStatus', {
      imgSrc: warning,
      header: t('the_changes_were_not_saved'),
      subHeader: t('try_again_later_2'),
      primaryButton: {
        label: t('close_2'),
        onClick: () => {
          handleCloseModal();
        },
      },
    }));
  };

  const handleSubmitSuccess = async () => {
    if (onSuccess) {
      await onSuccess();
    }

    dispatch(actions.setCurrentModal('reIssueOrReplaceMoneycardStatus', {
      imgSrc: tenbisFlyCard,
      header: t('your_new_card_is_on_the_way'),
      subHeader: t(isReIssue ? 'current_card_still_active_new_card_on_the_way_to_you' : 'the_current_card_is_cancelled_and_new_card_will_be_sent_to_you_notification_sent_to_email_xxx', {email: userData?.email}),
      primaryButton: {
        label: t('great_thanks'),
        onClick: () => {
          handleCloseModal();
        },
      },
    }));
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    trackEvent(isReIssue ? 'mcWorn_submit' : 'mcLost_submit');
    if (isReIssue) {
      try {
        await reIssueMoneycard(moneycardId, Number(selectedAddressId));
        await handleSubmitSuccess();
      } catch (error) {
        await handleSubmitError(error, onSubmit);
      } finally {
        setIsSubmitting(false);
      }
    }
    if (!isReIssue) {
      dispatch(setCurrentModal('moneycardFeedbackModal', {
        imgSrc: warningImage,
        imgAlt: t('before_you_cancel_your_card'),
        header: t('before_you_cancel_your_card'),
        subHeader: t('once_confirmed_your_card_will_be_canceled_and_can_no_longer_be_used_a_new_card_will_be_sent_to_your_selected_address'),
        primaryButton: {
          label: t('confirm'),
          onClick: async () => {
            const replaceMoneycardHandler = async () => {
              await replaceMoneycard(moneycardId, Number(selectedAddressId));
            };
            try {
              await replaceMoneycardHandler();
              await handleSubmitSuccess();
            } catch (error) {
              await handleSubmitError(error, replaceMoneycardHandler);
            } finally {
              setIsSubmitting(false);
            }
          },
        },
        secondaryButton: {
          label: t('close_3'),
          onClick: () => dispatch(setCurrentModal(null)),
        },
      }));
    }
  };

  return (
    <Root onClickOutside={handleCloseModal}>
      <Header>
        <Title id="modal-title">
          {t(isReIssue ? 'card_worn_or_not_working' : 'card_stolen_or_lost_modal_title')}
        </Title>
        <CloseButton onClick={handleCloseModal} />
      </Header>

      <ListWrapper>
        <SubTitle>{t('to_which_address_to_send_the_new_card')}</SubTitle>
        <List>
          {addresses.map(address => (
            <AddressItem key={address.addressId} onClick={() => setSelectedAddressId(String(address.addressId))}>
              <IconAndAddress>
                {address.isCompanyAddress && <OfficeIcon />}
                {!address.isCompanyAddress && <HouseIcon />}
                <AddressText>{formatAddress({address, lng: 'he'})}</AddressText>
              </IconAndAddress>
              <div>
                <RadioButton
                  name="addressList"
                  id={`address-${address.addressId}`}
                  isChecked={String(selectedAddressId) === String(address.addressId)}
                  value={address.addressId}
                  variant="check"
                />
              </div>
            </AddressItem>
          ))}
        </List>
      </ListWrapper>

      <StyledPieButton disabled={!selectedAddressId || isSubmitting} onClick={onSubmit}>{t('send_to_address')}</StyledPieButton>
    </Root>
  );
};

ReIssueOrReplaceMoneycardModal.hideCloseButton = true;

attachModal('reIssueOrReplaceMoneycard', ReIssueOrReplaceMoneycardModal);

export default ReIssueOrReplaceMoneycardModal;
